import type { Board } from '@/logic/board';
import type { Color, Move } from '@/logic/pieces';
import { movesForPiece } from '@/logic/pieces';

export class ComputerMoveService {
  static getComputerMove(board: Board, currentTurn: Color): Move | null {
    const validMoves: Move[] = [];
    
    // Find all pieces of the computer's color
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        const tile = board[y][x];
        if (tile.piece && tile.piece.color === currentTurn) {
          const moves = movesForPiece({ 
            piece: tile.piece, 
            board, 
            propagateDetectCheck: true 
          });
          validMoves.push(...moves);
        }
      }
    }
    
    if (validMoves.length === 0) {
      return null;
    }

    // Prioritize moves by type for smarter play
    const captureMoves = validMoves.filter(move => move.type === 'capture' || move.type === 'captureKing');
    const checkMoves = validMoves.filter(move => {
      // Check if this move puts the opponent in check
      const newBoard = board.map(row => row.map(tile => ({ ...tile })));
      const fromTile = newBoard[move.piece.position.y][move.piece.position.x];
      const toTile = newBoard[move.newPosition.y][move.newPosition.x];
      
      if (fromTile.piece) {
        toTile.piece = { ...fromTile.piece, position: move.newPosition };
        fromTile.piece = null;
        
        // Check if this puts opponent in check
        for (let y = 0; y < 8; y++) {
          for (let x = 0; x < 8; x++) {
            const tile = newBoard[y][x];
            if (tile.piece && tile.piece.color === currentTurn) {
              const moves = movesForPiece({ 
                piece: tile.piece, 
                board: newBoard, 
                propagateDetectCheck: false 
              });
              // Check if any move can capture the opponent's king
              if (moves.some(m => m.type === 'captureKing')) {
                return true;
              }
            }
          }
        }
      }
      return false;
    });
    
    const normalMoves = validMoves.filter(move => 
      move.type === 'valid' && 
      !captureMoves.includes(move) && 
      !checkMoves.includes(move)
    );

    // Choose move priority: captures > checks > normal moves
    if (captureMoves.length > 0) {
      // Prefer capturing higher value pieces
      const pieceValues = { pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 1000 };
      captureMoves.sort((a, b) => {
        const aValue = pieceValues[a.capture?.type || 'pawn'] || 0;
        const bValue = pieceValues[b.capture?.type || 'pawn'] || 0;
        return bValue - aValue; // Higher value first
      });
      return captureMoves[0];
    }
    
    if (checkMoves.length > 0) {
      return checkMoves[Math.floor(Math.random() * checkMoves.length)];
    }
    
    if (normalMoves.length > 0) {
      // Prefer center control and pawn advancement for black
      normalMoves.sort((a, b) => {
        const aScore = this.getMoveScore(a, 'black');
        const bScore = this.getMoveScore(b, 'black');
        return bScore - aScore;
      });
      return normalMoves[0];
    }
    
    // Fallback to random move
    return validMoves[Math.floor(Math.random() * validMoves.length)];
  }

  private static getMoveScore(move: Move, color: Color): number {
    let score = 0;
    
    // Prefer center control
    const centerX = move.newPosition.x;
    const centerY = move.newPosition.y;
    if (centerX >= 2 && centerX <= 5 && centerY >= 2 && centerY <= 5) {
      score += 2;
    }
    
    // Prefer pawn advancement for black (moving up the board)
    if (move.piece.type === 'pawn' && color === 'black') {
      score += (7 - move.newPosition.y) * 2; // Higher score for advancing pawns
    }
    
    // Prefer developing pieces (knights, bishops) early
    if (move.piece.type === 'knight' || move.piece.type === 'bishop') {
      score += 1;
    }
    
    return score;
  }

  static async makeComputerMove(
    board: Board, 
    setBoard: (board: Board) => void,
    currentTurn: Color,
    setTurn: () => void
  ): Promise<void> {
    const move = this.getComputerMove(board, currentTurn);
    
    if (move) {
      // Simulate a delay to make the computer move feel more natural
      // Longer delay for more complex moves
      const delay = move.type === 'capture' || move.type === 'captureKing' ? 800 : 600;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Apply the move
      const newBoard = board.map(row => row.map(tile => ({ ...tile })));
      
      // Find the piece to move
      const fromTile = newBoard[move.piece.position.y][move.piece.position.x];
      const toTile = newBoard[move.newPosition.y][move.newPosition.x];
      
      if (fromTile.piece) {
        const piece = fromTile.piece; // Store reference to avoid null checks
        // Check if this is a pawn promotion
        const isPawnPromotion = move.type === 'valid' && piece.type === 'pawn' && 
          (move.newPosition.y === 0 || move.newPosition.y === 7);
        
        // Move the piece
        if (isPawnPromotion) {
          // Create a proper queen piece for promotion
          toTile.piece = {
            type: 'queen' as const,
            color: piece.color,
            id: piece.id + 1,
            getId: () => `queen-${piece.color}-${piece.id + 1}`,
            position: move.newPosition,
            hasMoved: true
          };
        } else {
        toTile.piece = {
            ...piece,
          position: move.newPosition,
          hasMoved: true
        };
        }
        fromTile.piece = null;
        
        // Handle castling
        if (move.castling) {
          const rookTile = newBoard[move.castling.rook.position.y][move.castling.rook.position.x];
          const rookNewTile = newBoard[move.castling.rookNewPosition.y][move.castling.rookNewPosition.x];
          
          if (rookTile.piece) {
            rookNewTile.piece = {
              ...rookTile.piece,
              position: move.castling.rookNewPosition,
              hasMoved: true
            };
            rookTile.piece = null;
          }
        }
        
        setBoard(newBoard);
        setTurn();
      }
    }
  }
} 